// src-lit/components/product/kd-pantry-cart-add.ts
import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { SELECTORS } from '../../constants/selectors';
import type { KdPantryProducts } from './kd-pantry-products';
import {
  type PantryCartApiErrorResponse,
  buildPantryAddPayload,
  buildPantryCartItems,
  calcPantryDonationMoney,
  calcPantryGrandTotal,
  createPantryKey,
  parsePantryCartError,
} from './pantry-cart-add.logic';

const ERROR_GENERIC = 'カートへの追加に失敗しました。';
// 親商品（えらんで BOX）が在庫切れのときに、無反応にせず理由を伝えるメッセージ（M11）。
const ERROR_SOLDOUT = '申し訳ございません。この商品は在庫切れのため購入できません。';
// 重複時 clearCart → addCart で add が失敗したケース専用メッセージ。
// この経路ではカート内の既存パントリー商品が消えた状態でユーザーがフォームに留まるため、
// 既定メッセージだけだと「何が起きたか分からない」状況になる（#3185 レビュー指摘）。
const ERROR_AFTER_CLEAR =
  'カートへの追加に失敗しました。カート内のパントリー商品はリセットされたため、もう一度商品を選択してください。';
// v1 spinner CSS (.product-form__submit.loading) と連携する状態クラス
const LOADING_CLASS = 'loading';
const CART_ADD_URL = '/cart/add.js';
const CART_CLEAR_URL = '/cart/clear.js';
const CART_PAGE_URL = '/cart';
const CART_POST_HEADERS = {
  'Content-Type': 'application/json',
  Accept: 'application/javascript',
} as const;

interface ShopifyProductGlobal {
  id: number;
  price: number;
  type: string;
  variant?: { id: number };
}

declare global {
  interface Window {
    sendYappliEvent?: (
      eventName: string,
      params: Record<string, string>,
    ) => void;
    Shopify?: { product?: ShopifyProductGlobal };
  }
}

/**
 * @element kd-pantry-cart-add
 * @description パントリー商品（えらんでKuradashi）のカート追加 submit ハンドラ。
 *   v1 `<product-pantry-cart-button-wrap>` (assets/product-form.js:632-794) の Lit 移行版（#3090）。
 *
 *   - 内側の `[data-pantry-submit]` クリックを購読
 *   - <kd-pantry-products> から選択中エントリ・親子データ属性を読み取り、親 1 件 + 子 N 件を
 *     1 リクエストで `/cart/add.js` に bulk add（items[]）
 *   - properties: 支援先団体 / 支援金額（総額 1%） / _pantryKey / _パントリータイプ / 賞味期限
 *   - カートに同じパントリーケースがある場合は `/cart/clear.js` で既存カートを空にしてから add
 *   - 成功時に Yappli へ add_to_cart イベントを送り `/cart` へフルリロード遷移
 *   - 422 / その他エラー時はメッセージ表示 + 全 `[data-pantry-submit]` ボタンを再 enable
 *
 *   HTML は Liquid（v2-cart-button-pantry / v2-product-pantry-fixed-footer）が生成し、
 *   本コンポーネントは振る舞いのみ。ページ内に 2 インスタンス（PC 右カラム + SP 固定フッター）
 *   が同時に存在する想定で、クリック中は document スコープで全ボタンを disabled にして
 *   多重投入を防ぐ。
 */
@customElement('kd-pantry-cart-add')
export class KdPantryCartAdd extends LitElement {
  createRenderRoot() {
    return this;
  }

  private submitButton: HTMLButtonElement | null = null;

  render() {
    return nothing;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.submitButton = this.querySelector<HTMLButtonElement>(
      SELECTORS.PANTRY_CART_ADD.SUBMIT,
    );
    this.submitButton?.addEventListener('click', this.onClick);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.submitButton?.removeEventListener('click', this.onClick);
    this.submitButton = null;
  }

  private onClick = async (event: Event): Promise<void> => {
    event.preventDefault();

    const button = this.submitButton;
    if (!button || button.classList.contains(LOADING_CLASS)) return;

    const root = this.findPantryProductsRoot();
    if (!root) return;

    const entries = root.getSelectedEntries();
    if (entries.length === 0) return;

    const parentVariantId = Number(root.dataset.productVariantId);
    if (!Number.isFinite(parentVariantId) || parentVariantId <= 0) return;

    const parentInventory = Number(root.dataset.productInventoryQuantity);
    if (Number.isFinite(parentInventory) && parentInventory <= 0) {
      // 在庫切れ時にサイレント return するとボタンを押しても無反応で、ユーザーが
      // 理由を把握できない。在庫切れである旨のフィードバックを表示する（M11）。
      this.showError(ERROR_SOLDOUT);
      return;
    }

    this.hideError();
    this.setAllSubmitsLoading(true);

    // 重複時 clearCart 成功後の addCart 失敗を区別するため、clearCart 完了を track する。
    let cartCleared = false;

    try {
      const grandTotal = calcPantryGrandTotal(entries);
      const items = buildPantryCartItems({
        parentVariantId,
        entries,
        pantryKey: createPantryKey(),
        supportGroup: this.readSupportGroup(),
        donationAmount: calcPantryDonationMoney(grandTotal),
      });
      const payload = buildPantryAddPayload({
        items,
        sectionsUrl: window.location.pathname,
      });

      const isDuplicate = root.dataset.isDuplicatePantryItemInCart === 'true';
      if (isDuplicate) {
        await this.clearCart();
        cartCleared = true;
      }
      await this.addCart(payload);

      const totalQuantity = entries.reduce((acc, e) => acc + e.quantity, 0);
      this.sendYappli(totalQuantity);
      window.location.href = CART_PAGE_URL;
    } catch (error) {
      const fallback = cartCleared ? ERROR_AFTER_CLEAR : ERROR_GENERIC;
      const message =
        error instanceof Error && error.message && !cartCleared
          ? error.message
          : fallback;
      this.showError(message);
      this.setAllSubmitsLoading(false);
    }
  };

  private findPantryProductsRoot(): KdPantryProducts | null {
    return document.querySelector<KdPantryProducts>(
      SELECTORS.PANTRY_CART_ADD.PANTRY_PRODUCTS_ROOT,
    );
  }

  /**
   * 支援先団体 select の値を取得する。
   * select は v2-product-donation-picker (col_center) で render され、<kd-pantry-cart-add>
   * の外側に存在するため document スコープで探索する（<kd-cart-add-form> も同様）。
   */
  private readSupportGroup(): string {
    const select = document.querySelector<HTMLSelectElement>(
      SELECTORS.CART_ADD_FORM.DONATION_SELECT,
    );
    return select?.value ?? '';
  }

  private async clearCart(): Promise<void> {
    const response = await fetch(CART_CLEAR_URL, {
      method: 'POST',
      headers: CART_POST_HEADERS,
    });
    if (!response.ok) throw new Error(ERROR_GENERIC);
  }

  private async addCart(payload: object): Promise<void> {
    const response = await fetch(CART_ADD_URL, {
      method: 'POST',
      headers: CART_POST_HEADERS,
      body: JSON.stringify(payload),
    });
    const data = (await response.json().catch(() => ({}))) as PantryCartApiErrorResponse;
    if (!response.ok || data.status) {
      throw new Error(parsePantryCartError(data, ERROR_GENERIC));
    }
  }

  private sendYappli(quantity: number): void {
    if (typeof window.sendYappliEvent !== 'function') return;
    const product = window.Shopify?.product;
    if (!product) return;
    // 計測失敗はカート追加成功フロー（/cart 遷移）の妨げにならないよう握りつぶす。
    try {
      window.sendYappliEvent('add_to_cart', {
        price: product.price.toString(),
        content_id: `${product.id}-${product.variant?.id ?? ''}`,
        content_type: product.type,
        currency: 'JPY',
        quantity: quantity.toString(),
      });
    } catch {
      // no-op
    }
  }

  /**
   * ページ内の全 `[data-pantry-submit]` ボタン（PC + SP 固定フッター）に loading 状態を
   * 同期する。多重投入と「片方だけ無効化されて見える」UX 不整合を防ぐ。
   */
  private setAllSubmitsLoading(loading: boolean): void {
    const buttons = document.querySelectorAll<HTMLButtonElement>(
      SELECTORS.PANTRY_CART_ADD.SUBMIT,
    );
    buttons.forEach((button) => {
      if (loading) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.classList.add(LOADING_CLASS);
      } else {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        button.classList.remove(LOADING_CLASS);
      }
    });
  }

  private showError(message: string): void {
    const wrapper = this.querySelector<HTMLElement>(
      SELECTORS.PANTRY_CART_ADD.ERROR,
    );
    const text = this.querySelector<HTMLElement>(
      SELECTORS.PANTRY_CART_ADD.ERROR_MESSAGE,
    );
    if (!wrapper || !text) return;
    text.textContent = message;
    wrapper.hidden = false;
  }

  private hideError(): void {
    const wrapper = this.querySelector<HTMLElement>(
      SELECTORS.PANTRY_CART_ADD.ERROR,
    );
    const text = this.querySelector<HTMLElement>(
      SELECTORS.PANTRY_CART_ADD.ERROR_MESSAGE,
    );
    if (!wrapper || !text) return;
    text.textContent = '';
    wrapper.hidden = true;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-pantry-cart-add': KdPantryCartAdd;
  }
}
